import { useGridApiContext, GridColumnsPanelProps } from '@mui/x-data-grid-pro';
import { GroupedGridColDef } from './gridColumnTypes';
import { Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { SearchGlass } from '../../partials/SearchGlass';

export const CustomColumnsPanel = (props: GridColumnsPanelProps & { columnGroupSortOrder?: string[] }) => {
  const { columnGroupSortOrder } = props;
  const apiRef = useGridApiContext();
  const columns = apiRef.current.getAllColumns() as GroupedGridColDef[];
  const columnVisibilityModel = apiRef.current.state.columns.columnVisibilityModel;
  const initialColumnVisibilityModel = useMemo(() => apiRef.current.state.columns.columnVisibilityModel, []);
  const [searchValue, setSearchValue] = useState('');

  const isDirty = useMemo(() => {
    const allKeys = new Set([...Object.keys(columnVisibilityModel), ...Object.keys(initialColumnVisibilityModel)]);
    for (const key of allKeys) {
      if ((columnVisibilityModel[key] ?? true) !== (initialColumnVisibilityModel[key] ?? true)) {
        return true;
      }
    }
    return false;
  }, [columnVisibilityModel, initialColumnVisibilityModel]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleColumnToggle = (field: string) => {
    const isVisible = columnVisibilityModel[field] ?? true;
    apiRef.current.setColumnVisibility(field, !isVisible);
  };

  const handleReset = () => {
    apiRef.current.setColumnVisibilityModel(initialColumnVisibilityModel);
  };

  const handleShowHideAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const shouldShowAll = event.target.checked;
    // A column the grid does not have yet, such as a custom field still loading, keeps what the model says about it.
    const newVisibilityModel: { [key: string]: boolean } = { ...columnVisibilityModel };
    columns.forEach((col) => {
      if (col.hideable !== false) {
        newVisibilityModel[col.field] = shouldShowAll;
      }
    });
    apiRef.current.setColumnVisibilityModel(newVisibilityModel);
  };

  const filteredColumns = columns.filter((column) =>
    (column.headerName || column.field).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allHideableColumns = columns.filter((c) => c.hideable !== false);
  const allVisible = allHideableColumns.every((c) => columnVisibilityModel[c.field] ?? true);
  const allHidden = allHideableColumns.every((c) => columnVisibilityModel[c.field] === false);

  // Custom fields come as 'Ungrouped', so a grid with no catalog groups lists its columns under no heading, as MUI's did.
  const showGroupHeadings = columns.some((column) => column.columnGroup && column.columnGroup !== 'Ungrouped');
  const groupedColumns: { [key: string]: GroupedGridColDef[] } = {};
  const normalizedSortOrder = columnGroupSortOrder?.map((group) => (group === 'Ungrouped' ? 'Custom' : group));
  filteredColumns.forEach((column) => {
    const group = column.columnGroup === 'Ungrouped' || !column.columnGroup ? 'Custom' : column.columnGroup;
    if (!groupedColumns[group]) {
      groupedColumns[group] = [];
    }
    groupedColumns[group].push(column);
  });

  const sortedGroups = Object.keys(groupedColumns).sort((a, b) => {
    if (a === 'Custom') return 1;
    if (b === 'Custom') return -1;

    if (normalizedSortOrder) {
      const indexA = normalizedSortOrder.indexOf(a);
      const indexB = normalizedSortOrder.indexOf(b);

      if (indexA > -1 && indexB > -1) {
        return indexA - indexB;
      }
      if (indexA > -1) return -1;
      if (indexB > -1) return 1;
    }

    return a.localeCompare(b);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'auto', width: '100%' }}>
      <Box sx={{ p: 2, pb: 1, mr: 1, flexShrink: 0 }}>
        <div className="relative ml-2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <SearchGlass />
          </div>
          <input
            onChange={handleSearchChange}
            type="search"
            id="search"
            name="search"
            className="pl-10 pr-4 py-1.5 text-sm w-full border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
            placeholder="Search"
          />
        </div>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, pt: 1 }}>
        <FormGroup>
          {sortedGroups.length > 0 ? (
            sortedGroups.map((group) => (
              <Box key={group}>
                {showGroupHeadings && (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ mt: 0.5, color: 'text.secondary' }}>
                      {group.toLocaleUpperCase()}
                    </Typography>
                    <Divider sx={{ mt: 0.5, mb: 1 }} />
                  </>
                )}
                {groupedColumns[group].map((column) => (
                  <Box
                    key={column.field}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      key={column.field}
                      control={
                        <Checkbox
                          sx={{ pt: 0.75, pb: 1, pl: 1, pr: 0.5 }}
                          checked={columnVisibilityModel[column.field] ?? true}
                          onChange={() => handleColumnToggle(column.field)}
                          disabled={column.hideable === false}
                        />
                      }
                      label={column.headerName || column.field}
                    />
                  </Box>
                ))}
              </Box>
            ))
          ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: '1rem' }}>No columns</Typography>
          )}
        </FormGroup>
      </Box>
      {sortedGroups.length > 0 && (
        <Box
          className="MuiDataGrid-columnsManagementFooter"
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            p: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allVisible}
                indeterminate={!allVisible && !allHidden}
                onChange={handleShowHideAll}
              />
            }
            label="Show/Hide All"
          />
          <Button
            onClick={handleReset}
            size="small"
            variant="text"
            disabled={!isDirty}>
            RESET
          </Button>
        </Box>
      )}
    </Box>
  );
};
